import { useCallback, useEffect, useState } from "react";
import { apiGet, buildQueryString } from "lib/apiClient";

const SEARCH_DEBOUNCE_MS = 300;
const EXPORT_MAX_ROWS = 5000;

export default function usePaginatedResource(path, { pageSize: initialPageSize = 20, extraParams = {}, itemsKey, pollMs } = {}) {
  const [pageSize, setPageSizeState] = useState(initialPageSize);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  // The query itself only ever reads `debouncedSearch` — `search` is what the input binds
  // to, so typing feels instant, while the actual network request (and the page-1 reset
  // below) waits for a short pause, rather than firing one request per keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [search]);

  const load = useCallback(
    async (background = false) => {
      if (!background) setLoading(true);
      setError(null);
      try {
        const query = buildQueryString({ page, pageSize, search: debouncedSearch || undefined, ...extraParams });
        const result = await apiGet(`${path}${query}`);
        setItems(result[itemsKey] || []);
        setTotal(result.total || 0);
      } catch (err) {
        // A background poll failing silently (a blip, an expired session about to
        // redirect anyway) should not blank out data the admin is currently reading.
        if (!background) setError(err.message || "Something went wrong.");
      } finally {
        if (!background) setLoading(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [path, page, pageSize, debouncedSearch, JSON.stringify(extraParams), reloadToken],
  );

  useEffect(() => {
    load();
  }, [load]);

  // Money and its status (deposits, withdrawals, revenue, settlements) is confirmed by
  // a provider webhook on its own schedule — a tab left open would otherwise show a
  // stale figure until the admin manually reloads. Polling is opt-in (pollMs) and only
  // page 1 with no active search is considered "the live view" worth refreshing
  // underneath someone without disturbing whatever they've paged/filtered into.
  useEffect(() => {
    if (!pollMs) return undefined;
    if (page !== 1 || debouncedSearch) return undefined;
    const id = setInterval(() => load(true), pollMs);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollMs, page, debouncedSearch, load]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, JSON.stringify(extraParams)]);

  const refetch = useCallback(() => setReloadToken((t) => t + 1), []);

  // Exporting the whole result set (not just the loaded page) means paging through the
  // server's own page-size cap ourselves. Capped at EXPORT_MAX_ROWS so a huge, unfiltered
  // table can't turn one click into thousands of sequential requests.
  const exportAll = useCallback(async () => {
    const fetchPageSize = 100;
    const maxPages = Math.ceil(EXPORT_MAX_ROWS / fetchPageSize);
    const all = [];
    for (let p = 1; p <= maxPages; p++) {
      const query = buildQueryString({ page: p, pageSize: fetchPageSize, search: debouncedSearch || undefined, ...extraParams });
      const result = await apiGet(`${path}${query}`);
      const batch = result[itemsKey] || [];
      all.push(...batch);
      const knownTotal = result.total ?? total;
      if (batch.length < fetchPageSize || all.length >= knownTotal) break;
    }
    return all;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, itemsKey, debouncedSearch, JSON.stringify(extraParams)]);

  // Changing rows-per-page must return to page 1 — staying on e.g. page 7 of a 20-row
  // layout is out of range once the page holds 100.
  const setPageSize = (n) => { setPageSizeState(n); setPage(1); };
  return { items, total, loading, error, page, setPage, search, setSearch, pageSize, setPageSize, refetch, exportAll };
}
